<template>
	<div
		:class="['loader', ...variantClassList]"
		v-if="visible"
		data-testid="loader"
		:style="{ zIndex: zIndex }"
	>
		<div class="loader__animation">
			<div class="loader__piece"></div>
			<div class="loader__piece"></div>
			<div class="loader__piece"></div>
			<div class="loader__piece"></div>
		</div>
	</div>
</template>

<script>
import variantClasses from './variant-classes/loader-variant-classes';
import componentVariantMixin from '@/mixins/component-variant-mixin';
/**
 * To use the loader, add the loader-mixin to your component.
 * Then call this.showLoader() to show it and this.hideLoader() to hide it.
 */
export default {
	name: 'Loader',
	mixins: [componentVariantMixin],
	props: {
		visible: {
			type: Boolean,
			default: true
		},
		zIndex: {
			// custom z-index of the backdrop
			type: Number,
			default: 12000 // This value corresponds with $z-level6: 12000;
		}
	},
	created() {
		this.variantClasses = variantClasses;
	}
};
</script>

<style lang="scss" scoped>
.loader {
	position: relative;
	display: inline-flex;
	align-items: center;
	margin: 0 auto;

	// Default Page Loader - Square Slider
	&--square-slider {
		$size: 75px;
		$item-size: calc($size / 2.5);
		$animation-duration: 5s;
		padding: $padding-xs;

		.loader {
			&__animation {
				position: relative;
				width: $size;
				height: $size;
				transform: rotate(45deg);
				transform-origin: 26px 43px;
			}

			&__piece {
				position: absolute;
				width: $item-size;
				height: $item-size;
				border: 4px solid var(--square-color);

				&:nth-child(1) {
					--square-color: var(--brand-1);
					top: 0;
					left: 0;
					animation: animSquare1Animation $animation-duration linear
						infinite;
				}

				&:nth-child(2) {
					--square-color: var(--brand-2);
					top: 0;
					left: $item-size;
					animation: animSquare2Animation $animation-duration linear
						infinite;
				}

				&:nth-child(3) {
					--square-color: var(--brand-2-tint);
					top: $item-size;
					left: $item-size;
					animation: animSquare3Animation $animation-duration linear
						infinite;
				}
			}
		}

		@keyframes animSquare1Animation {
			0%,
			8.33%,
			16.66%,
			100% {
				transform: translate(0%, 0%);
			}

			24.99%,
			33.32%,
			41.65% {
				transform: translate(100%, 0%);
			}

			49.98%,
			58.31%,
			66.64% {
				transform: translate(100%, 100%);
			}

			74.97%,
			83.30%,
			91.63% {
				transform: translate(0%, 100%);
			}
		}

		@keyframes animSquare2Animation {
			0%,
			8.33%,
			91.63%,
			100% {
				transform: translate(0%, 0%);
			}

			16.66%,
			24.99%,
			33.32% {
				transform: translate(0%, 100%);
			}

			41.65%,
			49.98%,
			58.31% {
				transform: translate(-100%, 100%);
			}

			66.64%,
			74.97%,
			83.30% {
				transform: translate(-100%, 0%);
			}
		}

		@keyframes animSquare3Animation {
			0%,
			83.30%,
			91.63%,
			100% {
				transform: translate(0, 0);
			}

			8.33%,
			16.66%,
			24.99% {
				transform: translate(-100%, 0);
			}

			33.32%,
			41.65%,
			49.98% {
				transform: translate(-100%, -100%);
			}

			58.31%,
			66.64%,
			74.97% {
				transform: translate(0, -100%);
			}
		}
	}

	// Alternate Page Loader - Square Exploder
	&--square-exploder {
		$animation-duration: 1.2s;
		$size: 60px;
		height: 120px;
		width: 120px;
		position: relative;

		.loader__animation {
			@include transform-full-center;
			width: $size;
			height: $size;
			transform: translate(-50%, -50%) rotate(45deg);
			animation: loaderAnimation 1.2s infinite ease-in-out;
		}

		.loader__piece {
			position: absolute;
			display: block;
			width: calc($size / 2);
			height: calc($size / 2);
			background-color: var(--brand-2);
			animation: loaderBlockAnimation $animation-duration infinite
				ease-in-out both;

			&:nth-child(1) {
				top: 0;
				left: 0;
			}
			&:nth-child(2) {
				top: 0;
				right: 0;
				animation: loaderBlockInverseAnimation $animation-duration
					infinite ease-in-out both;
			}
			&:nth-child(3) {
				bottom: 0;
				left: 0;
				animation: loaderBlockInverseAnimation $animation-duration
					infinite ease-in-out both;
			}
			&:nth-child(4) {
				bottom: 0;
				right: 0;
			}
		}

		@keyframes loaderAnimation {
			0%,
			10%,
			100% {
				width: $size;
				height: $size;
			}
			65% {
				width: $size * 1.5;
				height: $size * 1.5;
			}
		}

		@keyframes loaderBlockAnimation {
			0%,
			30% {
				transform: rotate(0);
			}
			55% {
				background-color: var(--brand-2-tint);
			}
			100% {
				transform: rotate(90deg);
			}
		}

		@keyframes loaderBlockInverseAnimation {
			0%,
			20% {
				transform: rotate(0);
			}
			55% {
				background-color: var(--brand-1);
			}
			100% {
				transform: rotate(-90deg);
			}
		}
	}

	// Infinite Scroller Loader - Dots
	&--dots {
		$animation-duration: 1.2s;

		.loader__animation {
			display: flex;
		}

		.loader__piece {
			position: relative;
			width: calc($base-font-size/2);
			height: calc($base-font-size/2);
			margin: $gutter-lg;
			border-radius: 50%;

			&::before {
				position: absolute;
				content: '';
				width: 100%;
				height: 100%;
				background: inherit;
				border-radius: inherit;
				animation: waveAnimation $animation-duration ease-out infinite;
			}

			&:nth-child(even) {
				background: var(--brand-1);
			}

			&:nth-child(odd) {
				background: var(--brand-2);
			}

			@for $i from 1 through 4 {
				&:nth-child(#{$i}) {
					&::before {
						animation-delay: $i * 0.2s;
					}
				}
			}
		}

		@keyframes waveAnimation {
			50%,
			75% {
				transform: scale(2.5);
			}

			80%,
			100% {
				opacity: 0;
			}
		}
	}

	// Circle Loader
	&--circle {
		width: 100px;
		height: 100px;
		border-radius: 50%;
		border: 7px solid var(--bg-2);
		border-right-color: var(--neutral-4);
		animation: circle 1.5s linear infinite;

		@keyframes circle {
			0% {
				transform: rotate(0deg);
			}

			100% {
				transform: rotate(360deg);
			}
		}
	}
}
</style>
